import type { FC } from 'react';
import { memo, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import HypothesisSectionGroup from './HypothesisSectionGroup';
import HypothesisGroupCard from './HypothesisGroupCard';
import type {
  HypothesisCreativeEntry,
  HypothesisGroup,
  HypothesisSection,
} from '../../types/hypothesis';

const NO_HYPOTHESIS_KEY = 'No hypothesis';

interface CreativesGroupedViewProps {
  thisWeek: HypothesisGroup[];
  upcoming: HypothesisGroup[];
  noHypothesis: HypothesisCreativeEntry[];
  selectedCreatives: string[];
  columns: GridColDef[];
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onSectionChange: (hypothesis: string, section: HypothesisSection) => void;
}

const CreativesGroupedViewImpl: FC<CreativesGroupedViewProps> = ({
  thisWeek,
  upcoming,
  noHypothesis,
  selectedCreatives,
  columns,
  onSelectionChange,
  onSectionChange,
}) => {
  const noHypothesisSelectedIds = useMemo(() => {
    if (noHypothesis.length === 0) return [];
    const selectionSet = new Set(selectedCreatives);
    return noHypothesis.filter((c) => selectionSet.has(c.id)).map((c) => c.id);
  }, [noHypothesis, selectedCreatives]);

  return (
    <>
      <HypothesisSectionGroup
        title="This week"
        groups={thisWeek}
        selectedCreatives={selectedCreatives}
        columns={columns}
        onSelectionChange={onSelectionChange}
        onSectionChange={onSectionChange}
      />
      <HypothesisSectionGroup
        title="Upcoming"
        groups={upcoming}
        selectedCreatives={selectedCreatives}
        columns={columns}
        onSelectionChange={onSelectionChange}
        onSectionChange={onSectionChange}
      />
      {noHypothesis.length > 0 && (
        <HypothesisGroupCard
          hypothesis={NO_HYPOTHESIS_KEY}
          creatives={noHypothesis}
          section={null}
          showSectionSelector={false}
          selectedIds={noHypothesisSelectedIds}
          columns={columns}
          onSelectionChange={onSelectionChange}
        />
      )}
    </>
  );
};

const CreativesGroupedView = memo(CreativesGroupedViewImpl);

export default CreativesGroupedView;
